/**
 * Centralized API contract types shared by the HTTP client and feature services.
 *
 * The backend exposes two response shapes:
 *  - Domain endpoints wrap payloads in an envelope: `{ success, data, errors }`.
 *  - Platform endpoints (e.g. `/api/v1/app/*`) and middleware blocks (426/423/503)
 *    return flat payloads with no envelope.
 *
 * `EnvelopeMode` lets each request declare how its body should be interpreted. The
 * default (`auto`) preserves the historical duck-typed behavior: unwrap only when
 * all three envelope keys are present, otherwise return the payload untouched.
 * The base types, the pure detection helper, and the HTTP client wiring are all
 * part of phase C.0 (the envelope foundation); the version-aware phases C.1+ build
 * on top of it.
 */

/** Standard backend success/error envelope used by domain endpoints. */
export type ApiEnvelope<T> = {
  success: boolean;
  data: T | null;
  errors: unknown;
};

/**
 * Per-request envelope handling strategy.
 *  - `auto`: detect the envelope and unwrap it when present; pass flat payloads through.
 *  - `unwrap`: require an envelope; throw when `success === false`.
 *  - `raw`: never unwrap; return the parsed JSON as-is (PDF/blob/auth/migrations).
 */
export type EnvelopeMode = 'auto' | 'unwrap' | 'raw';

/** Default mode applied when a caller does not specify one. Keeps current behavior. */
export const DEFAULT_ENVELOPE_MODE: EnvelopeMode = 'auto';

/** Structural type guard: true only when the value carries all three envelope keys. */
export const isApiEnvelope = <T>(value: unknown): value is ApiEnvelope<T> =>
  Boolean(
    value && typeof value === 'object' && 'success' in value && 'data' in value && 'errors' in value
  );
